import type { DspModuleDescriptor } from '../../packages/dsp-module-contract/src/index.js';
import type { PatchConnectionV1, PatchGraphV1, PatchNodeV1 } from '../../packages/routing-graph/src/index.js';

export const ALLMOST_DEFAULT_PATCH_ID = 'allmost-default-2026-09-v1';
export const ALLMOST_DEFAULT_RUNTIME_ID = 'allmost-fixed-runtime-2026-09-v1';

export type DefaultModuleState = 'audible' | 'control' | 'muted' | 'bypassed' | 'ready' | 'tap';

export interface DefaultRuntimeModule {
  readonly moduleId: string;
  readonly state: DefaultModuleState;
  readonly role: string;
}

const audible = new Set(['mir.mod-001', 'mir.mod-019', 'mir.mod-021', 'mir.mod-034', 'mir.mod-025']);
const control = new Set(['mir.mod-006', 'mir.mod-014', 'mir.mod-015', 'mir.mod-016', 'mir.mod-017', 'mir.mod-018', 'mir.mod-035']);
const muted = new Set(['mir.mod-002', 'mir.mod-003', 'mir.mod-004', 'mir.mod-005', 'mir.mod-007', 'mir.mod-008', 'mir.mod-009', 'mir.mod-010', 'mir.mod-011', 'mir.mod-012', 'mir.mod-013']);
const bypassed = new Set(['mir.mod-020', 'mir.mod-022', 'mir.mod-023', 'mir.mod-024', 'mir.mod-026', 'mir.mod-027', 'mir.mod-028', 'mir.mod-029', 'mir.mod-030', 'mir.mod-031', 'mir.mod-032', 'mir.mod-033']);

const moduleRole = (moduleId: string): DefaultRuntimeModule => {
  const number = moduleId.slice(-3);
  if (audible.has(moduleId)) return Object.freeze({ moduleId, state: 'audible', role: number === '034' ? 'main mixer' : number === '025' ? 'final safety' : 'main audible chain' });
  if (control.has(moduleId)) return Object.freeze({ moduleId, state: 'control', role: 'control harness' });
  if (muted.has(moduleId)) return Object.freeze({ moduleId, state: 'muted', role: 'parked source lane' });
  if (bypassed.has(moduleId)) return Object.freeze({ moduleId, state: 'bypassed', role: 'parked processing/FX lane' });
  if (moduleId === 'mir.mod-036') return Object.freeze({ moduleId, state: 'tap', role: 'post-safety offline analyzer/export tap' });
  return Object.freeze({ moduleId, state: 'ready', role: 'available' });
};

export const ALLMOST_DEFAULT_RUNTIME = Object.freeze({
  id: ALLMOST_DEFAULT_RUNTIME_ID,
  patchId: ALLMOST_DEFAULT_PATCH_ID,
  graphEditsApplyToAudio: false,
  activeAudioRoute: Object.freeze(['mir.mod-001', 'mir.mod-019', 'mir.mod-021', 'mir.mod-034', 'mir.mod-025'] as const),
  activeControlRoute: Object.freeze(['mir.mod-015', 'mir.mod-017', 'mir.mod-019'] as const),
  notes: 'Fixed runtime. Patch Bay edits are visual and locally persisted; they do not recompile audio yet.',
});

const parameters: Readonly<Record<string, Readonly<Record<string, number>>>> = Object.freeze({
  'mir.mod-001': Object.freeze({ frequency: 110, waveform: 2, 'pulse-width': 0.5 }),
  'mir.mod-002': Object.freeze({ level: 0 }),
  'mir.mod-003': Object.freeze({ 'output-gain': 0 }),
  'mir.mod-005': Object.freeze({ mode: 1, 'width-samples': 4, level: 0 }),
  'mir.mod-007': Object.freeze({ gain: 0 }),
  'mir.mod-008': Object.freeze({ 'output-gain': 0 }),
  'mir.mod-009': Object.freeze({ 'output-gain': 0 }),
  'mir.mod-010': Object.freeze({ gate: 0, gain: 0 }),
  'mir.mod-011': Object.freeze({ gate: 0, gain: 0 }),
  'mir.mod-012': Object.freeze({ active: 0 }),
  'mir.mod-013': Object.freeze({ density: 0, gain: 0 }),
  'mir.mod-015': Object.freeze({ waveform: 0, 'rate-mode': 0, 'rate-hz': 0.5, polarity: 0 }),
  'mir.mod-018': Object.freeze({ bpm: 120, swing: 0, probability: 1, seed: 20260907 }),
  'mir.mod-019': Object.freeze({ mode: 0, 'cutoff-hz': 1200, q: 1.2, 'key-track': 1 }),
  'mir.mod-023': Object.freeze({ mix: 0 }),
  'mir.mod-025': Object.freeze({ 'threshold-db': -18, ratio: 4, 'lookahead-ms': 5, 'limiter-ceiling-db': -1, 'soft-clip-drive': 1 }),
  'mir.mod-026': Object.freeze({ 'bit-depth': 24, 'rate-hz': 192000, dither: 0, 'anti-alias-prefilter': 0 }),
  'mir.mod-027': Object.freeze({ rate: 1, 'pitch-cents': 0 }),
  'mir.mod-028': Object.freeze({ mix: 0 }),
  'mir.mod-029': Object.freeze({ mix: 0 }),
  'mir.mod-030': Object.freeze({ mix: 0 }),
  'mir.mod-031': Object.freeze({ mix: 0 }),
  'mir.mod-032': Object.freeze({ bypass: 1, dry: 1, wet: 0 }),
});

const cable = (id: string, source: string, sourcePort: string, target: string, targetPort: string): PatchConnectionV1 => Object.freeze({
  id,
  source: Object.freeze({ nodeId: source, portId: sourcePort }),
  target: Object.freeze({ nodeId: target, portId: targetPort }),
});

export const ALLMOST_DEFAULT_CONNECTIONS: readonly PatchConnectionV1[] = Object.freeze([
  cable('main-001-019', 'mod-001', 'out', 'mod-019', 'in'),
  cable('main-019-021', 'mod-019', 'out', 'mod-021', 'in'),
  cable('main-021-034', 'mod-021', 'out', 'mod-034', 'in'),
  cable('main-034-025', 'mod-034', 'out', 'mod-025', 'in'),
  cable('park-002-008', 'mod-002', 'out', 'mod-008', 'excitation'),
  cable('park-008-020', 'mod-008', 'left', 'mod-020', 'in'),
  cable('control-016-020', 'mod-016', 'out', 'mod-020', 'morph'),
  cable('park-003-009', 'mod-003', 'out', 'mod-009', 'excitation'),
  cable('park-009-022', 'mod-009', 'output', 'mod-022', 'in'),
  cable('park-004l-007l', 'mod-004', 'left', 'mod-007', 'in-l'),
  cable('park-004r-007r', 'mod-004', 'right', 'mod-007', 'in-r'),
  cable('park-007l-028l', 'mod-007', 'out-l', 'mod-028', 'in-left'),
  cable('park-007r-028r', 'mod-007', 'out-r', 'mod-028', 'in-right'),
  cable('control-018-005', 'mod-018', 'trigger', 'mod-005', 'trigger'),
  cable('park-005-024', 'mod-005', 'out', 'mod-024', 'in'),
  cable('park-024-023', 'mod-024', 'out', 'mod-023', 'in'),
  cable('park-023-026', 'mod-023', 'out', 'mod-026', 'in'),
  cable('park-026-027', 'mod-026', 'out', 'mod-027', 'in'),
  cable('park-027-029', 'mod-027', 'out', 'mod-029', 'in'),
  cable('park-010l-030l', 'mod-010', 'left', 'mod-030', 'in-left'),
  cable('park-010r-030r', 'mod-010', 'right', 'mod-030', 'in-right'),
  cable('park-011l-031l', 'mod-011', 'left', 'mod-031', 'in-left'),
  cable('park-011r-031r', 'mod-011', 'right', 'mod-031', 'in-right'),
  cable('park-012l-032l', 'mod-012', 'left', 'mod-032', 'in-left'),
  cable('park-012r-032r', 'mod-012', 'right', 'mod-032', 'in-right'),
  cable('park-013-033', 'mod-013', 'left', 'mod-033', 'in'),
  cable('control-018-014', 'mod-018', 'gate', 'mod-014', 'gate'),
  cable('control-014-006', 'mod-014', 'out', 'mod-006', 'density-envelope'),
  cable('control-006-008', 'mod-006', 'amplitude', 'mod-008', 'energy'),
  cable('control-015-017', 'mod-015', 'out', 'mod-017', 'in'),
  cable('control-017-019', 'mod-017', 'out', 'mod-019', 'note'),
  cable('control-035-015', 'mod-035', 'out', 'mod-015', 'reset'),
]);

const DEFAULT_POSITIONS: Readonly<Record<string, Readonly<{ x: number; y: number }>>> = Object.freeze({
  'mir.mod-001': { x: 40, y: 40 }, 'mir.mod-019': { x: 340, y: 40 }, 'mir.mod-021': { x: 640, y: 40 },
  'mir.mod-034': { x: 940, y: 40 }, 'mir.mod-025': { x: 1_240, y: 40 }, 'mir.mod-036': { x: 1_530, y: 40 },
  'mir.mod-002': { x: 40, y: 310 }, 'mir.mod-008': { x: 340, y: 310 }, 'mir.mod-020': { x: 640, y: 310 }, 'mir.mod-016': { x: 940, y: 310 },
  'mir.mod-003': { x: 40, y: 580 }, 'mir.mod-009': { x: 340, y: 580 }, 'mir.mod-022': { x: 640, y: 580 },
  'mir.mod-017': { x: 940, y: 580 }, 'mir.mod-015': { x: 1_240, y: 580 }, 'mir.mod-035': { x: 1_530, y: 580 },
  'mir.mod-004': { x: 40, y: 850 }, 'mir.mod-007': { x: 340, y: 850 }, 'mir.mod-028': { x: 640, y: 850 },
  'mir.mod-014': { x: 940, y: 850 }, 'mir.mod-006': { x: 1_240, y: 850 },
  'mir.mod-018': { x: 20, y: 1_120 }, 'mir.mod-005': { x: 270, y: 1_120 }, 'mir.mod-024': { x: 520, y: 1_120 },
  'mir.mod-023': { x: 770, y: 1_120 }, 'mir.mod-026': { x: 1_020, y: 1_120 }, 'mir.mod-027': { x: 1_270, y: 1_120 }, 'mir.mod-029': { x: 1_530, y: 1_120 },
  'mir.mod-010': { x: 40, y: 1_390 }, 'mir.mod-030': { x: 310, y: 1_390 }, 'mir.mod-011': { x: 580, y: 1_390 },
  'mir.mod-031': { x: 850, y: 1_390 }, 'mir.mod-012': { x: 1_120, y: 1_390 }, 'mir.mod-032': { x: 1_390, y: 1_390 },
  'mir.mod-013': { x: 40, y: 1_660 }, 'mir.mod-033': { x: 340, y: 1_660 },
});

export const createAllmostDefaultPatch = (descriptors: readonly DspModuleDescriptor[]): PatchGraphV1 => {
  if (descriptors.length !== 36) throw new Error(`default patch requires 36 descriptors, got ${descriptors.length}`);
  const nodes: readonly PatchNodeV1[] = Object.freeze(descriptors.map((descriptor) => Object.freeze({
    id: descriptor.id.replace('mir.', ''),
    moduleId: descriptor.id,
    parameters: parameters[descriptor.id] ?? Object.freeze({}),
    position: DEFAULT_POSITIONS[descriptor.id] ?? Object.freeze({ x: 40, y: 40 }),
  })));
  return Object.freeze({
    schemaVersion: 1,
    id: ALLMOST_DEFAULT_PATCH_ID,
    seed: 20260907,
    nodes,
    connections: ALLMOST_DEFAULT_CONNECTIONS,
  });
};

export const createDefaultRuntimeModules = (descriptors: readonly DspModuleDescriptor[]): readonly DefaultRuntimeModule[] =>
  Object.freeze(descriptors.map(({ id }) => moduleRole(id)));
